import { useToast } from "@bcc/hooks/use-toast";
import type { AdminProductUpdateResponse } from "@shared/types/admin-product-update";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalRequestResults } from "../products/approval-request-results";
import { setAdminUpdateToastTestId } from "./admin-product-edit.utils";

const HTTP_CONFLICT = 409;

/** The API answers 409 when a different change to the same thing is already awaiting approval. */
export const isPendingConflict = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("response" in error)) return false;
  const { response } = error;
  return typeof response === "object" && response !== null && "status" in response && response.status === HTTP_CONFLICT;
};

/**
 * Toasts for an admin update. Each SKU is its own Salesforce request, so a partial success names the SKUs that
 * failed rather than reporting the whole update as an error.
 */
export const useAdminUpdateToast = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const notifySuccess = useCallback(
    ({ results, failures }: AdminProductUpdateResponse) => {
      const total = results.length + failures.length;
      // Nothing new was sent when every SKU was already awaiting this exact change.
      const allAlreadyPending = failures.length === 0 && results.every((result) => result.alreadyPending);
      const successTitle = allAlreadyPending
        ? t("admin.productCatalogue.edit.alreadyPending")
        : t("admin.productCatalogue.edit.submitSuccess");
      toast({
        variant: failures.length > 0 ? "warning" : "success",
        title: failures.length > 0 ? t("admin.productCatalogue.edit.partialSuccess", { created: results.length, total }) : successTitle,
        description: (
          <span>
            <ApprovalRequestResults results={results} />
            {failures.length > 0 ? (
              <span className="mt-1 block" data-testid={setAdminUpdateToastTestId("failures")}>
                {t("admin.productCatalogue.edit.failedSkus", { itemNumbers: failures.map((failure) => failure.itemNumber).join(", ") })}
              </span>
            ) : null}
          </span>
        ),
      });
    },
    [t, toast]
  );

  const notifyError = useCallback(
    (error: unknown) =>
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: isPendingConflict(error)
          ? t("admin.productCatalogue.edit.pendingConflict")
          : t("admin.productCatalogue.edit.submitError"),
      }),
    [t, toast]
  );

  return { notifySuccess, notifyError };
};
